import { Checkbox } from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';

import { specimenFamily, type FontSpecimen } from '../model/font-specimen';
import { useFontFaces } from '../model/useFontFaces';

/**
 * The brand's typefaces, each drawn in itself.
 *
 * A specimen rather than `FontGrid`'s table: what someone checks about a font is what it looks like,
 * and a row of metadata cannot answer that. The table still suits a list view, where weight and
 * upload date are the columns being scanned.
 *
 * Clicking a card opens it and ticking its checkbox selects it, the two kept apart exactly as
 * `MediaGrid` keeps them: a person picks across both grids at once, and a font that answered a click
 * differently from the photograph beside it would read as a different kind of thing.
 */
const COLUMN_CLASS = {
  2: 'grid-cols-2',
  // Matches `MediaGrid`'s own fill, so font cards line up with the asset cards above them.
  fill: 'grid-cols-[repeat(auto-fill,minmax(180px,1fr))]',
} as const;

/**
 * The card's fill and edge, per state, taken from `MediaGrid` unchanged: one library, one selection,
 * so a font and a photograph have to answer a click the same way.
 */
const CARD_CLASS = {
  default: 'border-border bg-card hover:border-border-strong',
  open: 'border-primary bg-primary',
  selected: 'border-border bg-accent',
} as const;

/**
 * A specimen is drawn on the card's own surface rather than inset from it, so the filled state has to
 * turn the ink over with the fill or the font disappears into it. The selected fill is light enough
 * to leave the ordinary ink alone.
 */
const LABEL_CLASS = {
  onFill: 'text-primary-foreground/70',
  onCard: 'text-muted-foreground',
} as const;

const SAMPLE_CLASS = {
  onFill: 'text-primary-foreground',
  onCard: 'text-foreground',
} as const;

export const FontSpecimenGrid = ({
  specimens,
  columns = 2,
  selectedIds,
  openFontId,
  onChoose,
  onToggleSelect,
}: {
  specimens: readonly FontSpecimen[];
  /** A pair for a narrow panel; `fill` for a surface that owns the whole window. */
  columns?: 2 | 'fill';
  /**
   * Fonts ticked for a bulk action, by font id.
   *
   * Its presence rather than its contents is what puts the grid in picking mode, matching
   * `MediaGrid`: an empty set still means "picking, nothing ticked yet".
   */
  selectedIds?: ReadonlySet<string>;
  /** The font the detail panel is describing. Only ever one, and independent of what is ticked. */
  openFontId?: string;
  /**
   * Omit it for a grid that is there to be looked at rather than picked from. Cards then render as
   * plain figures: a button that did nothing on click would read as broken rather than as reference.
   */
  onChoose?: (fontId: string) => void;
  /** Ticking a card's checkbox. Omit it and no checkbox is drawn. */
  onToggleSelect?: (fontId: string) => void;
}) => {
  const { t } = useTranslation('tools');
  useFontFaces(specimens);

  if (specimens.length === 0) {
    return <p className="text-xs text-muted-foreground">{t('brandFont.empty')}</p>;
  }

  // The fill grid's cards are at least 180px across, which is the room a specimen needs to be read
  // as a specimen: square, named in full, and drawn large enough to judge. The pair in a narrow
  // panel is half that, so it keeps the small treatment -- an 80px "Aa" there is wider than the
  // card holding it.
  const large = columns === 'fill';
  const picking = selectedIds !== undefined;
  const interactive = onChoose !== undefined;

  return (
    <div data-testid="font-specimen-grid" className={`grid gap-2 ${COLUMN_CLASS[columns]}`}>
      {specimens.map((specimen) => {
        const selected = picking && selectedIds.has(specimen.id);
        const outlined = specimen.id === openFontId;
        // Open beats ticked where a card is both, as it does in the grid and the table: the tick in
        // the corner still says it is in the selection, where without the fill nothing would say the
        // panel is on it.
        const card = outlined
          ? CARD_CLASS.open
          : selected
            ? CARD_CLASS.selected
            : CARD_CLASS.default;

        const shared = {
          'data-testid': 'brand-font-specimen',
          // The same two fills a media tile carries, so a column holding both reads as one library
          // rather than two conventions side by side. What the tile also does -- inset its picture
          // on a card -- a specimen has no use for: that inset exists to keep the fill off a
          // photograph, and a font is drawn on the card's own surface, which the fill already is.
          // `isolate` for the same reason it is on a media tile: the sticky toolbar is also z-10.
          className: `group relative isolate flex w-full flex-col gap-1 overflow-hidden rounded-md border p-2 text-left transition-colors duration-fast ease-out ${card} ${large ? 'aspect-square justify-between' : ''}`,
        };

        const contents = (
          <>
            {/* Wrapped rather than truncated on a card with the height to spare -- a font is chosen
                by name, and half of "EmberModernDisplayStd-Regular" is not one. */}
            <span
              className={`text-xs ${outlined ? LABEL_CLASS.onFill : LABEL_CLASS.onCard} ${large ? 'break-words' : 'truncate'}`}
              title={specimen.label}
            >
              {specimen.label}
            </span>
            {/* The fallback is the interface font, so a card whose file has not arrived still shows a
                sample rather than collapsing to nothing. */}
            <span
              className={`${outlined ? SAMPLE_CLASS.onFill : SAMPLE_CLASS.onCard} ${large ? 'text-[80px] leading-none' : 'text-2xl leading-tight'}`}
              style={{
                fontFamily: `"${specimenFamily(specimen.id)}", var(--font-sans, sans-serif)`,
                ...(specimen.weight !== undefined ? { fontWeight: specimen.weight } : {}),
                ...(specimen.style !== undefined ? { fontStyle: specimen.style } : {}),
              }}
            >
              {t('brandFont.specimenSample')}
            </span>
          </>
        );

        if (!interactive) {
          return (
            <div key={specimen.id} {...shared}>
              {contents}
            </div>
          );
        }

        return (
          <div key={specimen.id} {...shared}>
            {contents}

            {/* An overlay rather than a card that is itself a button: the checkbox beside it is
                interactive too, and one cannot be nested in the other. */}
            <button
              type="button"
              aria-label={specimen.label}
              aria-current={outlined ? 'true' : undefined}
              className="absolute inset-0"
              onClick={() => onChoose(specimen.id)}
            />

            {/* Revealed on hover or focus and kept up once ticked, the way a media tile does it. */}
            {onToggleSelect !== undefined && (
              <span
                data-testid="font-specimen-check"
                data-checked={selected}
                className={`absolute right-1 top-1 z-10 transition-opacity duration-fast ease-out ${
                  selected
                    ? 'opacity-100'
                    : 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100'
                }`}
              >
                <Checkbox
                  aria-label={t('brandFont.selectFont', { name: specimen.label })}
                  checked={selected}
                  className="bg-background/80"
                  onCheckedChange={() => onToggleSelect(specimen.id)}
                />
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};
